import React, { useId, useState } from 'react';

import { useAccountRecord } from './AccountRecordContext';
import { AccountPageShell } from './AccountPageShell';
import { CardColumn, NotYetButton, Row, SmallButton } from './primitives';

/**
 * AR-380 — Password.
 *
 * v15 gave the password a page of its own, last in the column (D-ACCT-29), so
 * the three fields are open on arrival rather than hidden behind a button. The
 * fields are not a card draft — what the record keeps is the date.
 *
 * A member whose password manager set the current one cannot type it, so they
 * can ask for an emailed reset link instead (D-ACCT-47).
 *
 * Neither action has a backend yet, so both are NotYet rather than reporting a
 * change or a sent link that never happened.
 */

const FIELD =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';
const LABEL =
  'mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400';

const PasswordField: React.FC<{
  label: string;
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, autoComplete, value, onChange }) => {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        className={FIELD}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

export const AccountPassword: React.FC = () => {
  const { record } = useAccountRecord();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const clear = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  return (
    <AccountPageShell page="password">
      <CardColumn>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
          <div className="mb-3">
            <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
              Password
            </span>
          </div>
          <Row
            label="Last changed"
            value={record.you.pwChanged}
            last
          />

          <div className="mt-3 space-y-3 border-t border-gray-100 pt-3 dark:border-gray-800">
            <PasswordField
              label="Current password"
              autoComplete="current-password"
              value={current}
              onChange={setCurrent}
            />
            <PasswordField
              label="New password"
              autoComplete="new-password"
              value={next}
              onChange={setNext}
            />
            <PasswordField
              label="Confirm new password"
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
            />
            <div className="flex gap-2">
              <NotYetButton note="Password changes move into the platform with the Auth0 migration">
                Save new password
              </NotYetButton>
              <SmallButton onClick={clear}>Clear</SmallButton>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 text-[14px] text-gray-500 dark:border-gray-800 dark:text-gray-400">
            <span>Don&apos;t know your current password?</span>
            <NotYetButton note="Reset links move into the platform with the Auth0 migration">
              Email me a reset link
            </NotYetButton>
          </div>
        </div>
      </CardColumn>
    </AccountPageShell>
  );
};

export default AccountPassword;
